import React from 'react';
import { Table } from 'src/modules/shared/components/molecules/table/table';
import { type TableColumnProps } from 'src/modules/shared/components/molecules/table/table';
import {
  type CatalogProduct,
  type CustomFieldDefinition,
} from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import { Link } from 'src/modules/shared/components/atoms/links/link';
import { formatCurrency } from 'src/modules/shared/utils/formatting/currency.util';
import {
  PiDotsThreeVertical,
  PiMagnifyingGlass,
  PiPencilSimple,
  PiTableLight,
  PiTrash,
} from 'react-icons/pi';
import {
  TagDropdown,
  type TagDropdownOption,
} from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import { StyledIcon } from 'src/modules/shared/components/atoms/icons/styled-icon';
import { tableVariantClasses } from 'src/modules/shared/components/molecules/table/styles';
import { resolveDateCustomFieldIncludeTime } from 'src/modules/shared/utils/custom-field-definition.util';
import { getCustomFieldKey } from 'src/modules/shared/utils/custom-fields.util';
import { CustomFieldTableValue } from 'src/modules/shared/components/molecules/custom-fields/custom-field-table-value';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

export interface ProductsTableProps {
  products: CatalogProduct[];
  loading: boolean;
  isLoadingMore?: boolean;
  onEdit: (product: CatalogProduct) => void;
  onDelete: (product: CatalogProduct) => void;
  customFieldDefinitions?: CustomFieldDefinition[];
}

export const ProductsTable: React.FC<ProductsTableProps> = ({
  products,
  loading,
  isLoadingMore = false,
  onEdit,
  onDelete,
  customFieldDefinitions = [],
}) => {
  const { t } = useI18n();
  const { text } = tableVariantClasses.neutral;

  const baseColumns: TableColumnProps<CatalogProduct>[] = [
    {
      header: t('catalog.products.table.name'),
      accessor: 'name',
      render: (_value, product) => (
        <Text size="small" weight="medium">
          {product.name || '—'}
        </Text>
      ),
    },
    {
      header: t('catalog.products.table.description'),
      accessor: 'description',
      render: (_value, product) =>
        product.description ? (
          <Text size="tiny" color="secondary" className="line-clamp-3 max-w-xs">
            {product.description}
          </Text>
        ) : (
          <Text size="tiny" color="secondary">
            —
          </Text>
        ),
    },
    {
      header: t('catalog.products.table.price'),
      accessor: 'price',
      render: (_value, product) => (
        <Text size="tiny" color="secondary">
          {formatCurrency(product.price, product.currency ?? '', 0)}
        </Text>
      ),
    },
    {
      header: t('catalog.products.table.source'),
      accessor: 'sourceUrl',
      render: (_value, product) =>
        product.sourceUrl ? (
          <Link
            href={product.sourceUrl}
            external
            showExternalIcon
            size="tiny"
            underline="hover"
            truncate={40}
          >
            {product.sourceUrl}
          </Link>
        ) : (
          <Text size="tiny" color="secondary">
            —
          </Text>
        ),
    },
    {
      header: t('catalog.products.table.updated'),
      accessor: 'updatedAt',
      render: (_value, product) => {
        const updated =
          typeof product.updatedAt === 'string'
            ? new Date(product.updatedAt)
            : product.updatedAt;
        return (
          <Text size="tiny" color="secondary">
            {updated.toLocaleDateString()}
          </Text>
        );
      },
    },
    {
      // TODO: check when implementing i18n
      header: '',
      accessor: 'actions',
      render: (_value, product) => {
        const options: TagDropdownOption[] = [
          {
            id: 'edit',
            content: (
              <Text size="small" color="secondary">
                {t('catalog.products.table.actions.edit')}
              </Text>
            ),
            icon: <PiPencilSimple className="w-4 h-4 text-secondary-700" />,
          },
          {
            id: 'delete',
            content: (
              <Text size="small" color="danger">
                {t('catalog.products.table.actions.delete')}
              </Text>
            ),
            icon: <PiTrash className="w-4 h-4 text-danger-700" />,
          },
        ];

        return (
          <TagDropdown
            content={
              <div className="flex items-center">
                <PiDotsThreeVertical className="w-4 h-4" />
              </div>
            }
            variant="primary"
            appearance="soft"
            size="sm"
            placement="right-start"
            options={options}
            onOptionSelect={(option) => {
              if (option.id === 'edit') {
                onEdit(product);
              }
              if (option.id === 'delete') {
                onDelete(product);
              }
            }}
          />
        );
      },
    },
  ];

  const customColumns: TableColumnProps<CatalogProduct>[] =
    customFieldDefinitions.map((definition) => ({
      // TODO: check when implementing i18n
      header: (
        <div className="flex items-center gap-1.5">
          <StyledIcon
            icon={<PiTableLight className="w-3.5 h-3.5" />}
            variant="secondary"
            size="xs"
          />

          <Text size="small" weight="medium" className={text}>
            {definition.fieldLabel ?? definition.fieldKey}
          </Text>
        </div>
      ),
      accessor: `customFields.${definition.fieldKey}`,
      className: 'min-w-[140px]',
      render: (_value, product) => {
        if (!product.customFields) {
          return null;
        }

        const fieldKey = getCustomFieldKey(
          definition.fieldType,
          definition.fieldKey
        );
        const customFieldValues = product.customFields as Record<
          string,
          unknown
        >;
        const fieldValue = customFieldValues[fieldKey];

        return (
          <CustomFieldTableValue
            value={fieldValue}
            fieldType={definition.fieldType}
            includeTime={resolveDateCustomFieldIncludeTime(
              definition.fieldType,
              definition.includeTime
            )}
            variant="neutral"
          />
        );
      },
    }));

  const columns: TableColumnProps<CatalogProduct>[] = [
    ...baseColumns.slice(0, baseColumns.length - 1),
    ...customColumns,
    baseColumns[baseColumns.length - 1],
  ];

  return (
    <div className="flex flex-col gap-4">
      <Table<CatalogProduct>
        variant="neutral"
        columns={columns}
        data={products}
        keyExtractor={(product) => product.id.toString()}
        isLoading={loading && products.length === 0}
        isLoadingMore={isLoadingMore}
        // TODO: check when implementing i18n
        emptyStateMessage={t('catalog.products.table.emptyState.title')}
        emptyStateDescription={t(
          'catalog.products.table.emptyState.description'
        )}
        emptyStateIcon={<PiMagnifyingGlass className="w-full h-full" />}
      />
    </div>
  );
};
